import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiService, getStorageUrl } from '../../services/api';
import authService from '../../services/authService';
import { buildApiUrl } from '../../config/api';
import useSEO from '../../hooks/useSEO';
import sanitizeHtml from '../../utils/sanitizeHtml';
import { categoryUrl, courseUrl } from '../../utils/catalogUrls';
import Breadcrumbs from '../catalogo/Breadcrumbs';
import '../../styles/CursoDetalhe.css';

const stripHtmlForSEO = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
};

const CursoDetalhe = ({ curso: initialCurso = null }) => {
  const params = useParams();
  const id = initialCurso?.id ?? params.id;
  const [curso, setCurso] = useState(initialCurso);
  const [activeTab, setActiveTab] = useState('descricao');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const navigate = useNavigate();

  useSEO(curso ? {
    title: `${curso.title} | Curso Online Gratuito ABRALIBER`,
    description: `Faça o curso "${curso.title}" gratuitamente na ABRALIBER e solicite seu certificado digital de conclusão após terminar. ${stripHtmlForSEO(curso.description)}`,
    image: curso.imagem_principal?.image_url || curso.imagem_url || undefined,
    url: courseUrl(curso),
    type: 'article',
    keywords: `${curso.title}, ABRALIBER, curso grátis, certificado digital, curso online gratuito, extracurricular`,
  } : {});

  useEffect(() => {
    const fetchCurso = async () => {
      if (initialCurso) return;
      try {
        const response = await apiService.cursos.get(id);
        setCurso(response.data);
      } catch (error) {

        setError('Não foi possível carregar o curso. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    const checkEnrollment = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await axios.get(buildApiUrl('meus-cursos'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cursos = response.data || [];
        const enrolled = cursos.some((c) => String(c.id) === String(id));
        setIsEnrolled(enrolled);
      } catch {
        // silencioso — usuário não logado ou erro de rede
      }
    };

    fetchCurso();
    checkEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEnroll = async () => {
    try {
      const token = localStorage.getItem('token'); 
      if (!token) {
        navigate(`/curso/${id}/estudo`);
        return;
      }
      const response = await apiService.cursos.enroll({ curso_id: id });
      if (response.status === 201) {
        navigate(`/curso/${id}/estudo`);
      } else if (response.status === 200) {
        if(token){

          navigate(`/curso/${id}/estudo`);
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else {

      }
    }
  };

  if (!curso) {
    return <div>Carregando...</div>;
  }

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const categoriaPrincipal = curso.categorias?.[0];
  const breadcrumbs = [
    { name: 'Início', path: '/' },
    { name: 'Cursos', path: '/cursos' },
    ...(categoriaPrincipal ? [{ name: categoriaPrincipal.nome, path: categoryUrl(categoriaPrincipal) }] : []),
    { name: curso.title, path: courseUrl(curso) },
  ];

  return (
    <div className="curso-detalhe-page">
      {/* Hero Section */}
      <div className="curso-detalhe-hero">
        <div className="curso-detalhe-container">
          <h1>{curso.title}</h1>
          <p className="hero-subtitle">Curso gratuito e completo para seu desenvolvimento</p>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="curso-detalhe-container">
        <Breadcrumbs items={breadcrumbs} />
        <div className="curso-detalhe-content">
          {/* Informações do Curso */}
          <div className="curso-info">
            <h2 className="curso-info-title">{curso.title}</h2>
            
            <div className="curso-tabs">
              <button 
                className={`tab-button ${activeTab === 'descricao' ? 'active' : ''}`} 
                onClick={() => handleTabClick('descricao')}
              >
                Descrição
              </button>
              <button 
                className={`tab-button ${activeTab === 'certificado' ? 'active' : ''}`} 
                onClick={() => handleTabClick('certificado')}
              >
                Certificado
              </button>
              <button 
                className={`tab-button ${activeTab === 'vantagens' ? 'active' : ''}`} 
                onClick={() => handleTabClick('vantagens')}
              >
                Vantagens
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'descricao' && (
                <div className="tab-pane">
                  <h2>Descrição deste curso</h2>
                  <div
                    className="curso-description-html"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(curso.description) }}
                  />
                  {curso.modulos?.length > 0 && (
                    <>
                      <h3>Conteúdo programático</h3>
                      <ol className="curso-modulos-lista">
                        {curso.modulos.map((modulo) => (
                          <li key={modulo.id}>{modulo.title}</li>
                        ))}
                      </ol>
                    </>
                  )}
                </div>
              )}
              {activeTab === 'certificado' && (
                <div className="tab-pane">
                  <h2>Modelo do Nosso Certificado</h2>
                  <p>Veja abaixo nosso modelo do certificado de conclusão de curso emitido pela ABRALIBER | Associação Brasileira do Livre Saber.</p>
                  <h3>Frente:</h3>
                  <div style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
                    <img src="/certificado-modelo.png" alt="Modelo do Certificado ABRALIBER" className="certificado-image" style={{ width: '100%', maxWidth: '100%', height: 'auto', display: 'block', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', marginTop: '8px', boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}
              {activeTab === 'vantagens' && (
                <div className="tab-pane">
                  <h2>Vantagens deste curso</h2>
                  <p>✅ Certificado digital de conclusão opcional</p>
                  <p>✅ Conteúdo atualizado e relevante</p>
                  <p>✅ Acesso vitalício ao material</p>
                  <p>✅ Suporte especializado</p>
                  <p>✅ Flexibilidade de horários</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar com detalhes */}
          <div className="curso-sidebar">
            <img 
              src={curso.imagem_principal?.image_url || curso.imagem_url || getStorageUrl(curso.image) || '/default-course-image.svg'} 
              alt={curso.title} 
              className="curso-image" 
            />
            
            <div className="curso-stats">
              <div className="curso-stat">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>{curso.hours ? `${curso.hours} horas de conteúdo` : 'Carga horária não informada'}</span>
              </div>
              <div className="curso-stat">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span>Curso GRATUITO</span>
              </div>
              <div className="curso-stat">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V0h-2v2H7V0H5v2H3.5C2.67 2 2.01 2.67 2.01 3.5L2 19.5C2 20.33 2.67 21 3.5 21h17c.83 0 1.5-.67 1.5-1.5v-16C22 2.67 21.33 2 20.5 2z"/>
                </svg>
                <span>Certificado opcional</span>
              </div>
            </div>

            <button className="curso-button" onClick={handleEnroll}>
              {isEnrolled ? 'CONTINUAR CURSO' : 'COMEÇAR O CURSO'}
            </button>

            {curso.relacionados?.length > 0 && (
              <nav className="curso-relacionados" aria-label="Cursos relacionados">
                <h2>Cursos relacionados</h2>
                <ul>
                  {curso.relacionados.map((relacionado) => (
                    <li key={relacionado.id}>
                      <Link to={courseUrl(relacionado)}>{relacionado.title}</Link>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <div className="curso-share">
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://abraliber.com.br${courseUrl(curso)}`)}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Compartilhar no Facebook
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CursoDetalhe;
